import { Typography } from '@mui/joy';
import * as Sentry from '@sentry/react';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  createBrowserRouter,
  createRoutesFromElements,
  json,
  Link,
  Outlet,
  redirect,
  Route,
  RouterProvider,
  useNavigation,
  useRouteError,
} from 'react-router-dom';

import { WB_COMPANY_SLUG } from './assets/utils/axiosInstance';
import ErrorFramework from './components/common/errorFrameworkPopup';
import HubSpotLiveChat from './components/common/hubSpotLiveChat';
import RequireSecurePortal from './components/common/requireSecurePortal';
import UserTypeAgnosticAuth from './components/common/userTypeAgnosticAuth';
import RequestForInformation from './components/customer/dashboard/requestForInformation';
import GeneralLayout from './components/layout/generalLayout';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import ScrollToTop from './hooks/global/scrollToTop';
import { NavigatorWithConnection } from './interfaces/NavigatorWithConnection';
import CustomerDashboard from './Page/customer/customerDashboard';
import CustomerFinalReportDetailsPage from './Page/customer/customerFinalReportDetails';
import CustomerKnowledgebasePage from './Page/customer/customerKnowledgeBasePage';
import CustomerMeetingsPage from './Page/customer/customerMeetingsPage';
import CustomerReportsPage from './Page/customer/customerReportsPage';
import CustomerPage from './Page/customer/partner/customer/customerListPage';
import CustomerOverviewPage from './Page/customer/partner/customer/customerOverviewPage';
import CustomerUserInfoPage from './Page/customer/partner/customer/customerUserInfoPage';
import CustomerUserListPage from './Page/customer/partner/customer/customerUserListPage';
import ReactivationPage from './Page/customer/partner/settings/reactivationPage';
import CustomerSettingsFinalReport from './Page/customer/settingsPage/settingsFinalReport';
import CustomerSettingsSecurity from './Page/customer/settingsPage/settingsSecurity';
import KnowledgeBasePage from './Page/public/KnowledgeBasePage';
import LearnMorePage from './Page/public/learnMorePage';
import MaintenancePage from './Page/public/maintenancePage';
import MeetingCallPage from './Page/public/meetingCallPage';
import NotFoundPage from './Page/public/notFoundPage';
import SecurePortalPage from './Page/public/securedPortal/securePortalPage';
import ViewReportPage from './Page/public/viewReportPage';
import WhistleSettingsPage from './Page/whistleblower/settingsPage';
import { Layout as ReporterLayout } from './pages/reporter/layout';
import { AuthService } from './services/api/auth/endpoints';
import { AuthStoreSlice } from './slices/auth';
import { IntakeChannelSlice } from './slices/intakeChannelSlice';
import { fetchLanguage } from './slices/languageSlice';
import { store } from './utils/store';

import confideLogo from '~/assets/svg/confideLogoWhite.svg';
import Union from '~/assets/svg/Union.svg';
import {
  RequireRole,
  RequireAuth as RequireSafeAuth,
} from '~/utils/route-wrappers';

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const ForbiddenRoute = () => {
  const intl = useIntl();
  return (
    <NotFoundPage
      statusCode='403'
      statusMessage={intl.formatMessage({
        defaultMessage: 'You have no permission to access this page.',
      })}
    />
  );
};

function MaintenanceMode({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  if (import.meta.env.VITE_MAINTENANCE_MODE === 'true') {
    return <MaintenancePage />;
  }

  return <>{children}</>;
}

function Root({ children }: Readonly<{ children: React.ReactNode }>) {
  const transition = useNavigation();
  useEffect(() => {
    // when the state is idle then we can to complete the progress bar
    if (transition.state === 'idle') NProgress.done();
    // and when it's something else it means it's either submitting a form or
    // waiting for the loaders of the next location so we start it
    else NProgress.start();
  }, [transition.state]);

  // add Sentry network context on every error logged
  useEffect(() => {
    const nav = navigator as NavigatorWithConnection;
    if (nav.connection) {
      Sentry.setContext('network', {
        effectiveType: nav.connection.effectiveType, // connection type
        downlink: nav.connection.downlink, // download speed
        rtt: nav.connection.rtt, // round trip time / latency
      });
    }

    Sentry.setTags({
      device_memory: nav.deviceMemory?.toString() ?? 'unknown',
      user_agent: nav.userAgent,
    });
  }, []);

  return <>{children}</>;
}

const rootLoader = async () => {
  // Init auth state
  /**
   * It's important that this is called here to ensure that the auth state is initialized`
   * before we try to `update` or `set` any `auth` value like `region`
   */
  const isInitialized = store.getState().auth.isInitialized;
  if (!isInitialized) {
    store.dispatch(AuthStoreSlice.initCredentials());
  }

  // Init intake channel
  if (!store.getState().intakeChannelData.isInitialized) {
    store.dispatch(IntakeChannelSlice.actions.init());
  }

  if (WB_COMPANY_SLUG) {
    const regionQuery = store.dispatch(
      AuthService.endpoints.checkTenant.initiate({ tenant: WB_COMPANY_SLUG }),
    );
    const response = await regionQuery;
    if (response.data) {
      store.dispatch(
        AuthStoreSlice.setCredentials({ region: response.data.region }),
      );
    }
    regionQuery?.unsubscribe();
  }
  return true;
};

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route
      loader={rootLoader}
      element={
        <MaintenanceMode>
          <Root>
            <ScrollToTop />
          </Root>
        </MaintenanceMode>
      }
      errorElement={<ErrorBoundary />}
    >
      {/* Public Routes for Whistle Blower */}
      <Route
        path='/'
        lazy={() => import('~/pages/public/channel-selection/route')}
      />

      <Route path='/c'>
        <Route
          path=':channel'
          lazy={() => import('~/pages/public/channels/overview/route')}
        />
        <Route
          path=':channel/submit-report'
          lazy={() => import('~/pages/public/channels/new-report-form/route')}
        />
        <Route
          path=':channel/schedule-call'
          // element={<ScheduleCallPage />}
          lazy={() => import('~/pages/public/schedule-call')}
        />
      </Route>
      <Route
        lazy={() => safeLazyRoute(() => import('~/pages/customer/auth/layout'))}
      >
        <Route
          path='/login'
          lazy={() => safeLazyRoute(() => import('~/pages/public/auth/login'))}
        />
        <Route
          path='/register'
          lazy={() =>
            safeLazyRoute(() => import('~/pages/public/auth/register'))
          }
        />
        <Route
          path='/forget-password'
          lazy={() =>
            safeLazyRoute(() => import('~/pages/public/auth/forget-password'))
          }
        />
        <Route
          path='/googleAuthenticator'
          lazy={() =>
            safeLazyRoute(
              () => import('~/pages/public/auth/google-authenticator'),
            )
          }
        />
      </Route>
      <Route
        path='/:slug/:uniqueId/portal/request-for-information'
        element={<RequireSecurePortal />}
      >
        <Route element={<GeneralLayout />}>
          <Route index={true} element={<SecurePortalPage />} />
        </Route>
      </Route>
      <Route path='/learn-more' element={<LearnMorePage />} />
      <Route path='/knowledge-base' element={<KnowledgeBasePage />} />
      <Route
        path='/report-created'
        lazy={() => import('~/pages/public/report-created')}
      />

      {/* Public Routes for customer journey */}
      <Route
        lazy={() => safeLazyRoute(() => import('~/pages/customer/auth/layout'))}
      >
        <Route
          path='/customer/login'
          lazy={() =>
            safeLazyRoute(() => import('~/pages/customer/auth/login'))
          }
        />
        <Route
          path='/customer/otp'
          lazy={() => safeLazyRoute(() => import('~/pages/customer/auth/otp'))}
        />
        <Route
          path='/customer/googleAuthenticator'
          lazy={() =>
            safeLazyRoute(
              () => import('~/pages/customer/auth/google-authenticator'),
            )
          }
        />
        <Route
          path='/customer/google/callback'
          lazy={() =>
            safeLazyRoute(() => import('~/pages/customer/auth/google-callback'))
          }
        />
        <Route
          path='/customer/register/:id'
          lazy={() =>
            safeLazyRoute(() => import('~/pages/customer/auth/register'))
          }
        />
        <Route
          path='/customer/register'
          // We redirect to the register route with an invalid id to show the error page
          loader={() => redirect('/customer/register/INVITE_ID')}
        />
        <Route
          path='/:uid/:token/reset-password'
          lazy={() =>
            safeLazyRoute(() => import('~/pages/customer/auth/reset-password'))
          }
        />
        <Route
          path='/customer/forget-password'
          lazy={() =>
            safeLazyRoute(() => import('~/pages/customer/auth/forget-password'))
          }
        />
        <Route
          path='/meetings/join/:meetingID'
          lazy={() => import('~/pages/public/auth/guest-meeting-login')}
        />
      </Route>

      {/* Customer Journey Routes that requires Auth */}
      <Route element={<ErrorFramework isCustomer />}>
        <Route
          loader={() => {
            /**
             * This call was previously called inside the `RequireCustomerAuth`
             * but since this is not auth or authorization related, we moved it here.to trigger
             * the request when accessing any `customer` related pages
             */
            const DEFAULT_LANG = 'en';
            const isInit =
              !!store.getState().languages.userLocalizedLanguage.id ||
              !!store.getState().languages.userTranslationLanguage.id;

            if (isInit) return json({ ok: true });

            store.dispatch(
              fetchLanguage({
                type: 'translation',
                languageCode: DEFAULT_LANG,
              }),
            );
            return json({ ok: true });
          }}
          element={
            <RequireSafeAuth>
              <RequireRole
                roles={[
                  'TENANT_USER',
                  'TENANT_ADMIN',
                  'EXTERNAL_USER',
                  'PARTNER_USER',
                  'PARTNER_ADMIN',
                ]}
              >
                <Outlet />
              </RequireRole>
            </RequireSafeAuth>
          }
        >
          <Route lazy={() => import('~/pages/customer/layout')}>
            <Route element={<HubSpotLiveChat />}>
              <Route
                path='/customer'
                lazy={() => import('~/pages/customer/home/route')}
              />
              <Route
                path='/customer/reports/create-report'
                lazy={() =>
                  import('~/pages/customer/reports/create-report/route')
                }
              />
              <Route
                path='/customer/reports/create-report/:reportId'
                lazy={() =>
                  import('~/pages/customer/reports/create-report/success-form')
                }
              />
              <Route
                path='/customer/reports/:reportId'
                lazy={() =>
                  import('~/pages/customer/reports/report-details/route')
                }
              />
              <Route
                path='/customer/reports/:reportId/network-graph'
                lazy={() =>
                  safeLazyRoute(
                    () =>
                      import('~/pages/customer/reports/network-graph/route'),
                  )
                }
              />

              <Route
                path='/customer/reports'
                element={<CustomerReportsPage />}
              />
              <Route
                element={
                  <RequireRole
                    roles={[
                      'TENANT_USER',
                      'TENANT_ADMIN',
                      'PARTNER_USER',
                      'PARTNER_ADMIN',
                    ]}
                  >
                    <Outlet />
                  </RequireRole>
                }
              >
                <Route
                  path='/customer/reports/archives'
                  lazy={() =>
                    import('~/pages/customer/reports/archived-reports')
                  }
                />
                <Route>
                  <Route
                    path='/customer/meeting-list'
                    element={<CustomerMeetingsPage />}
                  />
                </Route>
                <Route
                  path='/customer/dashboard'
                  element={<CustomerDashboard />}
                />
                <Route
                  path='/customer/dashboard/request-for-information'
                  element={<RequestForInformation />}
                />
                <Route
                  path='/customer/knowledgebase'
                  element={<CustomerKnowledgebasePage />}
                />
                <Route
                  path='/customer/settings/program'
                  lazy={() => import('~/pages/customer/settings/program/route')}
                />
                {/* <Route
                  path='/customer/settings/workflow'
                  lazy={() =>
                    import('~/pages/customer/settings/workflow/route')
                  }
                /> */}
                <Route
                  path='/customer/settings/reportForm'
                  lazy={() =>
                    safeLazyRoute(
                      () =>
                        import(
                          '~/pages/customer/settings/report-form-v2/route'
                        ),
                    )
                  }
                />
                <Route
                  path='/customer/settings/security'
                  element={<CustomerSettingsSecurity />}
                />
                <Route
                  path='/customer/settings/templates'
                  element={<CustomerSettingsFinalReport />}
                />
                <Route
                  path='/customer/settings/templates/:id'
                  element={<CustomerFinalReportDetailsPage />}
                />
                <Route
                  path='/customer/settings/templates/processes/:id'
                  lazy={() =>
                    safeLazyRoute(
                      () =>
                        import(
                          '~/pages/customer/settings/final-report/processes/template-editor/route'
                        ),
                    )
                  }
                />

                <Route
                  path='/customer/settings/users'
                  lazy={() =>
                    safeLazyRoute(
                      () =>
                        import(
                          '~/pages/customer/settings/users/users-index/route'
                        ),
                    )
                  }
                />
                <Route
                  path='/customer/settings/users/:userId/update-access'
                  lazy={() =>
                    import(
                      '~/pages/customer/settings/users/edit-user-access/route'
                    )
                  }
                />
                {/* new reporting */}
                <Route path='/customer/reporting' element={<Outlet />}>
                  <Route
                    path='new'
                    // element={<CustomerReportingFactory page='new' />}
                    lazy={() =>
                      import('~/pages/customer/reporting/reporting-new/route')
                    }
                  />
                  <Route
                    index
                    // element={<CustomerReportingFactory page='' />}
                    lazy={() =>
                      import('~/pages/customer/reporting/reporting-index')
                    }
                  />
                </Route>
                <Route
                  path='/customer/reporting/:id'
                  lazy={() =>
                    import('~/pages/customer/reporting/reporting-view/route')
                  }
                />
                <Route
                  path='/customer/reports'
                  element={<CustomerReportsPage />}
                />

                <Route
                  path='/customer/settings/case-actions'
                  lazy={() =>
                    import('~/pages/customer/settings/case-actions/route')
                  }
                />

                {/* BACK OFFICE SIDE */}
                <Route
                  path='/customer/backoffice/customer'
                  element={<CustomerPage />}
                />
                <Route
                  path='/customer/backoffice/customer/overview/:slug'
                  lazy={() =>
                    import('~/pages/customer/back-office/customer/update/route')
                  }
                />
                <Route
                  path='/customer/backoffice/customer/:slug/users'
                  element={<CustomerUserListPage />}
                />
                <Route
                  path='/customer/backoffice/customer/:slug/user/:userId'
                  element={<CustomerUserInfoPage />}
                />
                <Route
                  path='/customer/backoffice/customer/overview'
                  element={<CustomerOverviewPage />}
                />
                <Route
                  path='/customer/backoffice/onboarding'
                  lazy={() =>
                    import('~/pages/customer/back-office/onboarding/route')
                  }
                />
                <Route
                  path='/customer/backoffice/settings/reactivation'
                  element={<ReactivationPage />}
                />
                <Route
                  path='/customer/backoffice/settings/process-template'
                  lazy={() =>
                    import(
                      '~/pages/customer/back-office/settings/process-templates/route'
                    )
                  }
                />
                <Route
                  path='/customer/backoffice/settings/process-template/:id'
                  lazy={() =>
                    safeLazyRoute(
                      () =>
                        import(
                          '~/pages/customer/settings/final-report/processes/template-editor/route'
                        ),
                    )
                  }
                />
              </Route>
            </Route>
          </Route>

          <Route
            path='customer/contact/customer-ticket'
            lazy={() =>
              import('~/pages/customer/contact/customer-ticket/route')
            }
          />
        </Route>
      </Route>

      {/* Routes that requires Auth */}
      <Route
        element={
          <RequireSafeAuth>
            <RequireRole roles={['WHISTLE_BLOWER']}>
              <ErrorFramework />
            </RequireRole>
          </RequireSafeAuth>
        }
      >
        <Route element={<ReporterLayout />}>
          <Route path='/inbox' lazy={() => import('~/pages/reporter/inbox')} />
          <Route
            path='/new-report'
            lazy={() => import('~/pages/reporter/create-report')}
          />

          <Route
            path='/drafts'
            lazy={() => import('~/pages/reporter/draft-reports')}
          />
          <Route
            path='/drafts/:reportId'
            lazy={() => import('~/pages/reporter/edit-draft-report')}
          />
          <Route
            path='/meeting-list'
            // element={<MeetingsPage />}
            lazy={() => import('~/pages/reporter/reporter-meetings')}
          />
          <Route
            path='/settings'
            // lazy={() => import('~/pages/reporter/settings')}
            element={<WhistleSettingsPage />}
          />
        </Route>
        <Route lazy={() => import('~/pages/reporter/report-details/layout')}>
          <Route path='/inbox/:reportDetailsId' element={<ViewReportPage />} />
        </Route>
      </Route>

      {/* Routes that requires Auth and is userType agnostic */}
      <Route element={<UserTypeAgnosticAuth />}>
        <Route path='/meetings/:meetingID' element={<MeetingCallPage />} />
      </Route>

      <Route
        path='/logout'
        lazy={() => safeLazyRoute(() => import('~/pages/logout'))}
      />
      <Route path='/forbidden' element={<ForbiddenRoute />} />
      {/* Catch All Route */}
      <Route path='*' lazy={() => import('~/pages/404')} />
      <Route
        path='/sentry-test-error-page'
        lazy={() => import('~/pages/sentry-test-page')}
      />
    </Route>,
  ),
);

function App() {
  return <RouterProvider router={router} />;
}

export default App;

/**
 * throws a custom "ERR_LAZY_LOAD" error when the lazy load fails.
 * We can't reliably just rely on the error message because it's not consistent between browsers.
 */
async function safeLazyRoute<T>(arg: () => Promise<T>) {
  return arg().catch(() => {
    throw new Error('ERR_LAZY_LOAD');
  });
}

function LazyRouteError() {
  return (
    <div className='h-screen flex flex-col relative justify-center items-center gap-4 p-4 sm:p-0'>
      <div
        aria-hidden='true'
        className='w-full h-full absolute inset-0'
        style={{
          background:
            'linear-gradient(270deg,  var(--joy-palette-primary-500) 0%,var(--joy-palette-secondary-600) 100%)',
        }}
      >
        <img src={Union} alt='' className='w-full absolute bottom-0' />
      </div>
      <div className='absolute inset-x-0 top-0 p-4 w-full flex justify-center items-center'>
        <Link to='/' className='h-10'>
          <img src={confideLogo} alt='Logo' className='h-full' />
        </Link>
      </div>
      <Card>
        <div className='text-center'>
          <Typography level='h4'>
            <FormattedMessage defaultMessage='Oops! Something went wrong' />
          </Typography>
          <Typography level='body-sm'>
            <FormattedMessage defaultMessage='An error occurred while trying to load the content. Please refresh and try again.' />
          </Typography>
        </div>
        <Button
          color='gradient-primary'
          onClick={() => window.location.reload()}
        >
          <FormattedMessage defaultMessage='Refresh' />
        </Button>
      </Card>
    </div>
  );
}

function ErrorBoundary() {
  const error = useRouteError();
  const isLazyLoadError =
    error instanceof Error && error.message === 'ERR_LAZY_LOAD';

  useEffect(() => {
    NProgress.done();
  }, []);

  if (isLazyLoadError) {
    return <LazyRouteError />;
  }

  return (
    <div className='h-screen flex flex-col justify-center items-center gap-4'>
      <div className='text-center'>
        <Typography level='h4'>
          <FormattedMessage defaultMessage='Oops! Something went wrong' />
        </Typography>
        <Typography level='body-sm'>
          <FormattedMessage defaultMessage='An error occurred. Please refresh and try again.' />
        </Typography>
      </div>
      <Button color='gradient-primary' onClick={() => window.location.reload()}>
        <FormattedMessage defaultMessage='Refresh' />
      </Button>
    </div>
  );
}
